// Dependencies
import React from 'react';

// Lib
import { Box, Link, Text, Stack, Type } from '@nerdwallet/currency';
import buildCityStateString from '../../../../lib/buildCityStateString';

// Local Components
import ColResultsTable from '../col-results-table/col-results-table';
import CityResultsError from '../../shared/city-results-error/city-results-error';
import {
  CitiesDataProps,
  CompareDataProps,
  ViewConfigProps,
} from '../../shared/types';

// CSS
import style from './col-results-body.module.less';

function getColPercentText(percentDifference, cityState) {
  if (percentDifference > 0) {
    return `${percentDifference}% higher in ${cityState}`;
  }
  return `${Math.abs(percentDifference)}% lower in ${cityState}`;
}

type ColResultsBodyProps = {
  citiesData: CitiesDataProps;
  compareData: CompareDataProps;
  handleInputChange: (newData: Record<string, any>) => void;
  resultsTableConfig: ViewConfigProps[];
};

const ColResultsBody = ({
  citiesData,
  compareData,
  handleInputChange,
  resultsTableConfig,
}: ColResultsBodyProps): JSX.Element => {
  const sourceCityState = buildCityStateString(citiesData.sourceCity);
  const destinationCityState = buildCityStateString(citiesData.destinationCity);
  const destinationHasCityLife = citiesData.destinationCity?.population > 0;
  const destinationCityName = citiesData.destinationCity?.name;
  const destinationCitySlug = citiesData.destinationCity?.slug;

  const sourceSchoolRating = `${citiesData.sourceCity?.greatschools_rating} / 10`;
  const destinationSchoolRating = `${citiesData.destinationCity?.greatschools_rating} / 10`;

  const showSchool =
    citiesData?.sourceCity?.greatschools_rating > 0 &&
    citiesData?.destinationCity?.greatschools_rating > 0;
  const colPercentText = getColPercentText(
    compareData?.colPercentDifference,
    destinationCityState
  );
  const adjustedIncome = compareData?.adjustedIncome;

  const modifiedCitiesData = {
    sourceCityState,
    destinationCityState,
    sourceCity: {
      ...citiesData.sourceCity,
      greatschools_rating: sourceSchoolRating,
    },
    destinationCity: {
      ...citiesData.destinationCity,
      greatschools_rating: destinationSchoolRating,
    },
  };

  const data = {
    compareData,
    citiesData: modifiedCitiesData,
    showSchool,
  };

  // The percent difference is calculated using the cities composite_index
  // If percent difference is NaN, this means we didn't receive the city data
  if (Number.isNaN(compareData?.colPercentDifference)) {
    // on results page, so update both source and destination city
    const onRefreshClick = () => {
      const newData = {
        inputs: {
          sourceCity: citiesData.sourceCity.id,
          destinationCity: citiesData.destinationCity.id,
        },
        name: 'costOfLivingCalculator',
      };
      handleInputChange(newData);
    };

    return <CityResultsError onRefreshClick={onRefreshClick} />;
  }

  return (
    <Box className={style.colResults}>
      <Box
        className={style.header}
        paddingTop={4}
        paddingBottom={3}
        marginTop={1}
        marginBottom={2}
        paddingLeft={{ desktop: 3 }}
      >
        <Box paddingBottom={1}>
          <Stack spacing={3}>
            <Text>{`To maintain your standard of living in ${destinationCityState}, you'll need a household income of:`}</Text>
            <Type size="4" fontFamily="gotham-medium">
              {adjustedIncome}
            </Type>
            <Text>{`The cost of living is ${colPercentText}`}</Text>
            {destinationHasCityLife && (
              <Link
                href={`/cost-of-living-calculator/city-life/${destinationCitySlug}`}
                target="_blank"
              >
                <Text size="small">{`See ${destinationCityName}’s complete City Life page`}</Text>
              </Link>
            )}
          </Stack>
        </Box>
      </Box>

      <ColResultsTable
        trackingFormName="col-results-table"
        data={data}
        viewConfig={resultsTableConfig}
      />
    </Box>
  );
};

ColResultsBody.displayName = 'ColResultsBody';

export default ColResultsBody;
